import { useEffect, useState, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { useApp } from "@/lib/AppContext";

const T = {
  ar: {
    badge: "آراء عملائنا",
    title: "ماذا يقول عملاؤنا",
    subtitle: "تقييمات حقيقية من عملائنا الكرام",
    empty: "لا توجد تقييمات بعد، كن أول من يقيّم!",
    copyLink: "📋 انسخ رابط التقييم",
    copied: "✅ تم النسخ!",
  },
  en: {
    badge: "Customer Reviews",
    title: "What Our Customers Say",
    subtitle: "Real ratings from our valued customers",
    empty: "No reviews yet, be the first to review!",
    copyLink: "📋 Copy Review Link",
    copied: "✅ Copied!",
  }
};

function Stars({ count }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(s => (
        <span key={s} className={`text-base ${s <= count ? "text-amber-400" : "text-gray-300 dark:text-gray-600"}`}>★</span>
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const { lang } = useApp();
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [current, setCurrent] = useState(0);
  const autoPlayRef = useRef(null);

  useEffect(() => {
    base44.entities.Review.filter({ is_approved: true }, "-created_date", 20)
      .then(data => { setReviews(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Auto-slide: RTL → go backwards (index--), LTR → go forwards (index++)
  useEffect(() => {
    if (reviews.length <= 1) return;
    autoPlayRef.current = setInterval(() => {
      setCurrent(prev => isAr
        ? (prev - 1 + reviews.length) % reviews.length
        : (prev + 1) % reviews.length
      );
    }, 3500);
    return () => clearInterval(autoPlayRef.current);
  }, [reviews.length, isAr]);

  const copyLink = () => {
    const url = `${window.location.origin}/rate`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  // Show 3 cards at a time (or fewer if not enough)
  const visibleCount = Math.min(3, reviews.length);
  const visibleReviews = reviews.length > 0
    ? Array.from({ length: visibleCount }, (_, i) => reviews[(current + i) % reviews.length])
    : [];

  return (
    <section id="reviews" className="py-20 bg-secondary/20 overflow-hidden" dir={isAr ? "rtl" : "ltr"}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-black px-4 py-2 rounded-full mb-3">
            {tr.badge}
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-foreground mb-2">{tr.title}</h2>
          <p className="text-muted-foreground">{tr.subtitle}</p>
          {avg && (
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="text-4xl font-black text-amber-600">{avg}</span>
              <div>
                <Stars count={Math.round(avg)} />
                <p className="text-xs text-muted-foreground">{reviews.length} {isAr ? "تقييم" : "reviews"}</p>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card rounded-2xl p-6 animate-pulse border border-border h-36" />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">{tr.empty}</p>
        ) : (
          <>
            {/* Slider */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {visibleReviews.map((r, idx) => (
                <div
                  key={`${r.id}-${idx}`}
                  className="bg-card border border-border rounded-2xl p-5 hover:shadow-md transition-all animate-in fade-in duration-500"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-lg font-black text-primary">
                        {r.customer_name?.charAt(0) || "؟"}
                      </div>
                      <div>
                        <p className="font-bold text-foreground text-sm">{r.customer_name}</p>
                        {r.order_number && <p className="text-[10px] text-muted-foreground">#{r.order_number}</p>}
                      </div>
                    </div>
                    {r.is_featured && <span className="text-amber-400 text-lg">⭐</span>}
                  </div>
                  <Stars count={r.rating} />
                  {r.comment && <p className="text-sm text-muted-foreground mt-2 leading-relaxed line-clamp-3">{r.comment}</p>}
                </div>
              ))}
            </div>

            {/* Dots indicator */}
            <div className="flex justify-center gap-1 mt-8">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  aria-label={isAr ? `عرض التقييم ${i + 1} من ${reviews.length}` : `Show review ${i + 1} of ${reviews.length}`}
                  aria-current={i === current ? "true" : "false"}
                  className="p-2 flex items-center justify-center rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                >
                  <span className={`block rounded-full transition-all ${i === current ? "w-5 h-2 bg-amber-400" : "w-2 h-2 bg-border hover:bg-amber-300"}`} />
                </button>
              ))}
            </div>
          </>
        )}


      </div>
    </section>
  );
}